import { useAuth } from "@/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { trpc } from "@/lib/trpc";
import { 
  CheckCircle2, 
  XCircle, 
  AlertCircle, 
  Building2, 
  User, 
  MapPin, 
  Briefcase,
  Database,
  RefreshCw,
  Link as LinkIcon
} from "lucide-react";
import { Link } from "wouter";
import { useState } from "react";
import { toast } from "sonner";
import { useTranslation } from 'react-i18next';
import { getLocalizedContent, type Language } from '@/lib/i18n-content';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * System Status / Account Info Page
 * Displays diagnostic information in a user-friendly way
 */
export default function SystemStatus() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as Language;
  const { user } = useAuth();
  const { data: profile, refetch: refetchProfile } = trpc.profile.get.useQuery();
  const { data: diagnosticData, isLoading, refetch } = trpc.calculator.diagnostic.useQuery();
  const { data: companies } = trpc.company.list.useQuery();
  
  // Load job details for translation
  const jobCode = diagnosticData?.companyActivity || diagnosticData?.profileData?.job_code;
  const { data: jobDetails } = trpc.jobs.getByCode.useQuery(
    { jobCode: jobCode || '' },
    { enabled: !!jobCode }
  );
  
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  
  // Mutation to link user to company
  const linkCompanyMutation = trpc.admin.updateUser.useMutation({
    onSuccess: (data) => {
      console.log('[Link Company] Success:', data);
      toast.success(t('systemStatus.successfully_linked_to_company'));
      refetch();
      refetchProfile();
      setSelectedCompanyId("");
    },
    onError: (error) => {
      console.error('[Link Company] Error:', error);
      toast.error(`Failed to link company: ${error.message}`);
    },
  });
  
  const handleLinkCompany = () => {
    if (!selectedCompanyId || !profile?.id) {
      toast.error(t('systemStatus.please_select_a_company'));
      return;
    }
    
    console.log('[Link Company] Mutation params:', {
      userId: profile.id,
      company_id: selectedCompanyId,
    });
    
    linkCompanyMutation.mutate({
      userId: profile.id,
      company_id: selectedCompanyId,
    });
  };

  const getStatusIcon = (condition: boolean) => {
    return condition ? (
      <CheckCircle2 className="h-5 w-5 text-green-500" />
    ) : (
      <XCircle className="h-5 w-5 text-red-500" />
    );
  };

  const getStatusBadge = (condition: boolean, trueText: string, falseText: string) => {
    return condition ? (
      <Badge className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300">
        {trueText}
      </Badge>
    ) : (
      <Badge variant="destructive">{falseText}</Badge>
    );
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  const hasCompany = !!diagnosticData?.companyId;
  const hasActivity = !!diagnosticData?.companyActivity;
  const hasMapping = (diagnosticData?.totalMappingEntries || 0) > 0;
  const isFullyConfigured = hasCompany && hasActivity && hasMapping;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t('systemStatus.title')}</h1>
            <p className="text-muted-foreground">
              {t('systemStatus.subtitle')}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('systemStatus.refresh')}
          </Button>
        </div>

        {/* Overall Status Alert */}
        {!isFullyConfigured && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t('systemStatus.not_fully_configured')}
            </AlertDescription>
          </Alert>
        )}

        {isFullyConfigured && (
          <Alert className="border-green-200 bg-green-50 dark:bg-green-900/10">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800 dark:text-green-300">
              {t('systemStatus.fully_configured')}
            </AlertDescription>
          </Alert>
        )}

        {/* User Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {t('systemStatus.user_information')}
            </CardTitle>
            <CardDescription>{t('systemStatus.your_personal_account_details')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">{t('settings.email')}</p>
                <p className="font-medium">{user?.email || t('systemStatus.not_available')}</p>
              </div>
              
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">{t('systemStatus.name')}</p>
                <p className="font-medium">
                  {profile?.first_name && profile?.last_name
                    ? `${profile.first_name} ${profile.last_name}`
                    : t('systemStatus.not_set')}
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">{t('systemStatus.role')}</p>
                <Badge variant="secondary">{user?.role || 'user'}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Company Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {t('systemStatus.company_configuration')}
            </CardTitle>
            <CardDescription>{t('systemStatus.your_company_and_activity_settings')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Company Link Status */}
            <div className="flex items-start justify-between p-4 border rounded-lg">
              <div className="flex items-start gap-3">
                {getStatusIcon(hasCompany)}
                <div>
                  <p className="font-medium">{t('systemStatus.company_link')}</p>
                  <p className="text-sm text-muted-foreground">
                    {hasCompany
                      ? `${t('systemStatus.linked_to')}: ${diagnosticData?.companyName}`
                      : t('systemStatus.no_company_assigned')}
                  </p>
                  {!hasCompany && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Profile company_id: <span className="font-mono">{diagnosticData?.companyId || 'NULL'}</span>
                    </p>
                  )}
                </div>
              </div>
              {getStatusBadge(hasCompany, t('systemStatus.connected'), t('systemStatus.not_connected'))}
            </div>

            {/* Activity Configuration */}
            <div className="flex items-start justify-between p-4 border rounded-lg">
              <div className="flex items-start gap-3">
                {getStatusIcon(hasActivity)}
                <div>
                  <p className="font-medium">{t('systemStatus.business_activity')}</p>
                  <p className="text-sm text-muted-foreground">
                    {hasActivity && jobDetails
                      ? getLocalizedContent({
                          en: jobDetails.subsector_en,
                          fr: jobDetails.subsector_fr,
                          ar: jobDetails.subsector_ar
                        }, currentLang)
                      : hasActivity
                      ? `${t('systemStatus.activity_code')}: ${diagnosticData?.companyActivity}`
                      : t('systemStatus.no_activity_code')}
                  </p>
                </div>
              </div>
              {getStatusBadge(hasActivity, t('systemStatus.configured'), t('systemStatus.not_configured'))}
            </div>

            {/* Additional Profile Fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  {t('settings.sector')}
                </p>
                <p className="font-medium">
                  {jobDetails ? getLocalizedContent({
                    en: jobDetails.sector_en,
                    fr: jobDetails.sector_fr,
                    ar: jobDetails.sector_ar
                  }, currentLang) : (diagnosticData?.profileData?.sector || t('systemStatus.not_set'))}
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  {t('settings.country')}
                </p>
                <p className="font-medium">{diagnosticData?.profileData?.country || t('systemStatus.not_set')}</p>
              </div>

              <div className="space-y-1">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  {t('systemStatus.job_code')}
                </p>
                <p className="font-mono text-sm">{diagnosticData?.profileData?.job_code || t('systemStatus.not_set')}</p>
              </div>
            </div>

            {/* Action Buttons */}
            {!hasCompany && user?.role === 'mypa_super_admin' && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <p className="font-medium mb-3">{t('systemStatus.link_to_company')}</p>
                  <p className="text-sm mb-4">
                    {t('systemStatus.select_company_to_link')}
                  </p>
                  <div className="flex gap-3">
                    <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder={t('systemStatus.select_a_company')} />
                      </SelectTrigger>
                      <SelectContent>
                        {companies && companies.length > 0 ? (
                          companies.map((company) => (
                            <SelectItem key={company.id} value={company.id}>
                              {company.name}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="no-companies" disabled>
                            No companies available
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <Button 
                      size="sm" 
                      onClick={handleLinkCompany}
                      disabled={!selectedCompanyId || linkCompanyMutation.isPending}
                    >
                      {linkCompanyMutation.isPending ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <LinkIcon className="h-4 w-4 mr-2" />
                      )}
                      {t('systemStatus.link_company')}
                    </Button>
                  </div>
                  <div className="mt-3 pt-3 border-t">
                    <Link href="/admin/companies">
                      <Button size="sm" variant="outline">
                        <Building2 className="h-4 w-4 mr-2" />
                        {t('systemStatus.manage_companies')}
                      </Button>
                    </Link>
                  </div>
                </AlertDescription>
              </Alert>
            )}
            
            {!hasCompany && user?.role !== 'mypa_super_admin' && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <p className="font-medium mb-2">{t('systemStatus.action_required')}</p>
                  <p className="text-sm">
                    {t('systemStatus.contact_administrator')}
                  </p>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Emission Factors Availability */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              {t('systemStatus.emission_factors_availability')}
            </CardTitle>
            <CardDescription>{t('systemStatus.data_available_for_calculations')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Mapping Status */}
            <div className="flex items-start justify-between p-4 border rounded-lg">
              <div className="flex items-start gap-3">
                {getStatusIcon(hasMapping)}
                <div>
                  <p className="font-medium">{t('systemStatus.emission_factors_mapping')}</p>
                  <p className="text-sm text-muted-foreground">
                    {hasMapping
                      ? `${diagnosticData?.totalMappingEntries} ${t('systemStatus.emission_factors_available')}`
                      : t('systemStatus.no_emission_factors')}
                  </p>
                </div>
              </div>
              {getStatusBadge(hasMapping, t('systemStatus.available'), t('systemStatus.not_available'))}
            </div>

            {/* Available Job Codes */}
            {diagnosticData?.mappingJobCodes && diagnosticData.mappingJobCodes.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{t('systemStatus.available_activity_codes')}</p>
                  <Badge variant="secondary">
                    {diagnosticData.mappingJobCodes.length} codes
                  </Badge>
                </div>
                <div className="max-h-[300px] overflow-y-auto space-y-2">
                  {diagnosticData.mappingJobCodes.map((item) => (
                    <div
                      key={item.jobCode}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        item.isMatch
                          ? 'bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-800'
                          : 'bg-background'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {item.isMatch && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                        <span className="font-mono text-sm">{item.jobCode}</span>
                        {item.isMatch && (
                          <Badge variant="outline" className="text-xs">{t('systemStatus.your_activity')}</Badge>
                        )}
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {item.feCount} factors
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Error Display */}
            {diagnosticData?.error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <p className="font-medium">{t('systemStatus.system_error')}</p>
                  <p className="text-sm mt-1">{diagnosticData.error}</p>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Next Steps */}
        {!isFullyConfigured && (
          <Card>
            <CardHeader>
              <CardTitle>{t('systemStatus.next_steps')}</CardTitle>
              <CardDescription>{t('systemStatus.what_you_need_to_do')}</CardDescription>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3">
                {!hasCompany && (
                  <li className="flex items-start gap-3">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                      1
                    </span>
                    <div>
                      <p className="font-medium">{t('systemStatus.get_assigned_to_company')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('systemStatus.contact_admin_to_link')}
                      </p>
                    </div>
                  </li>
                )}
                {hasCompany && !hasActivity && (
                  <li className="flex items-start gap-3">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                      {!hasCompany ? '2' : '1'}
                    </span>
                    <div>
                      <p className="font-medium">{t('systemStatus.configure_company_activity')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('systemStatus.company_needs_activity_code')}
                      </p>
                    </div>
                  </li>
                )}
                {hasCompany && hasActivity && !hasMapping && (
                  <li className="flex items-start gap-3">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                      1
                    </span>
                    <div>
                      <p className="font-medium">{t('systemStatus.upload_emission_factors')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('systemStatus.contact_admin_for_emission_factors')}
                      </p>
                    </div>
                  </li>
                )}
              </ol>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}

